import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../../../environments/environment';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { Menu } from '../../../../models/menu/dto/menu';
import { map, shareReplay } from 'rxjs/operators';
import { StringifyUtils } from '../../../../utils/stringify-utils';
import { MenuType } from '../../../../models/utils/dto/menu-type-definition';
import { CardLiveViewViewModel } from '../../shared/card-live-view-view-model';
import { exists } from '../../../../functions/exists';

@Injectable()
export class PrintCardLiveViewViewModel extends CardLiveViewViewModel {
  private sendPrintCardMenuSub: Subscription | null = null;
  private sendVariantIdsSub: Subscription | null = null;
  private sendMetadataSub: Subscription | null = null;

  private readonly _printCardMenu = new BehaviorSubject<Menu | null>(null);
  private readonly printCardMenuSource$ = this._printCardMenu as Observable<Menu | null>;
  public readonly printCardMenu$ = combineLatest([
    this.printCardMenuSource$.deepCopy$(),
    this.locationDisplayAttributesIndex$,
    this.companyDisplayAttributesIndex$,
    this.companyConfig$
  ]).pipe(
    map(([printCardMenu, locationDAIndex, companyDAIndex, companyConfig]) => {
      return printCardMenu?.adjustDataForLiveView(locationDAIndex, companyDAIndex, companyConfig);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  connectToPrintCardMenu = (printCardMenu: Menu | null) => this._printCardMenu.next(printCardMenu);

  /* *************************** Local Threads of Execution *************************** */

  private loadCompanyDisplayAttributesForLiveViewMenu = this.printCardMenuSource$.notNull().subscribeWhileAlive({
    owner: this,
    next: menu => this.loadCompanyDisplayAttributesForMenu(menu)
  });

  /* ********************************************************************************** */

  public readonly url$ = this.printCardMenu$.pipe(map(menu => this.generateLiveViewURL(menu?.type)));

  private readonly _variantIds = new BehaviorSubject<string[] | null>(null);
  public readonly variantIds$ = this._variantIds as Observable<string[] | null>;
  connectToVariantIds = (variantIds: string[] | null) => this._variantIds.next(variantIds);

  private readonly _metadata = new BehaviorSubject<any | null>(null);
  public readonly metadata$ = this._metadata as Observable<any | null>;
  connectToMetadata = (metadata: any | null) => this._metadata.next(metadata);

  protected generateLiveViewURL(menuType: MenuType): SafeResourceUrl {
    const cacheBuster = '?cacheBuster=' + Date.now() + Math.floor(Math.random() * 1000000);
    const env = environment.production && environment.environmentName !== 'rc' ? '' : `${environment.environmentName}.`;
    const menuTypeUrlText = menuType === MenuType.PrintLabelMenu ? 'print-label' : 'print-card';
    const url = `https://${env}display.mybudsense.com/#/${menuTypeUrlText}/live-view/${cacheBuster}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private sendPrintCardMenu(iFrame: HTMLIFrameElement): void {
    this.sendPrintCardMenuSub?.unsubscribe();
    this.sendPrintCardMenuSub = this.printCardMenu$.subscribeWhileAlive({
      owner: this,
      next: printCardMenu => {
        if (exists(printCardMenu)) {
          const msg = { printCardMenu: JSON.parse(JSON.stringify(printCardMenu, StringifyUtils.displayAppReplacer)) };
          iFrame?.contentWindow?.postMessage(msg, '*');
        }
      }
    });
  }

  private sendVariantIds(iFrame: HTMLIFrameElement): void {
    this.sendVariantIdsSub?.unsubscribe();
    this.sendVariantIdsSub = this.variantIds$.subscribeWhileAlive({
      owner: this,
      next: variantIds => {
        const msg = { variantIds };
        iFrame?.contentWindow?.postMessage(msg, '*');
      }
    });
  }

  private sendMetadata(iFrame: HTMLIFrameElement): void {
    this.sendMetadataSub?.unsubscribe();
    this.sendMetadataSub = this.metadata$.subscribeWhileAlive({
      owner: this,
      next: metadata => {
        const msg = { metadata: JSON.parse(JSON.stringify(metadata, StringifyUtils.displayAppReplacer)) };
        iFrame?.contentWindow?.postMessage(msg, '*');
      }
    });
  }

  public override sendDataToIFrame(iFrame: HTMLIFrameElement): void {
    super.sendDataToIFrame(iFrame);
    this.sendPrintCardMenu(iFrame);
    this.sendVariantIds(iFrame);
    this.sendMetadata(iFrame);
  }
}
